/* eslint-disable @typescript-eslint/array-type */
import { merge } from 'lodash';
import React from 'react';
import {
  HandleCreatorsStructure,
  ReactLifeCycleFunctionsThisArguments,
} from 'recompose';

// Shorten type name and set state to {}
export type LifeCycleThis<Props> = ReactLifeCycleFunctionsThisArguments<
  Props,
  {}
>;

export const extendWithDefaults = <T>(config: {
  props: T;
  defaultProps: Partial<T>;
}): T => {
  const { props, defaultProps } = config;

  return merge({}, defaultProps, props);
};

type VoidReturn<T extends (...params: any[]) => any> = T extends (
  ...params: infer R
) => any
  ? (...params: R) => void
  : never;

export type FuncHash = {
  [key: string]: (...args: any[]) => any;
};

export type Unwrap<T extends FuncHash> = {
  [P in keyof T]: ReturnType<T[P]>;
};

export type HandlerMethodTypes<
  PropsForHandlers,
  T extends HandleCreatorsStructure<PropsForHandlers>,
> =
  // NOTE: !!! Gerard, I'm commenting recent addition out, since it breaks all handlers that are
  // returning anything. We can get back to it once you are back from your vacation so that we could
  // discuss what you tried to accomplish.
  // ({ [P in keyof T]: VoidReturn<ReturnType<T[P]>>});
  { [P in keyof T]: ReturnType<T[P]> };

export type StateUpdaterMethodTypes<
  TState,
  TProps,
  T extends {
    [P in keyof T]: (
      state: TState,
      props: TProps,
    ) => (...rest: any[]) => Partial<TState> | undefined;
  },
> = { [P in keyof T]: VoidReturn<ReturnType<T[P]>> };

export type DeepPartial<T> = {
  // tslint:disable-next-line:prefer-array-literal
  [P in keyof T]?: T[P] extends Array<infer U>
    ? DeepPartial<U>[]
    : T[P] extends ReadonlyArray<infer U>
    ? ReadonlyArray<DeepPartial<U>>
    : DeepPartial<T[P]>;
};

export type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;

export type PropertyNames<T> = { [K in keyof T]: K }[keyof T];

export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<
  T,
  Exclude<keyof T, Keys>
> &
  {
    [K in Keys]-?: Required<Pick<T, K>>;
  }[Keys];

export type RequireOnlyOne<T, Keys extends keyof T = keyof T> = Pick<
  T,
  Exclude<keyof T, Keys>
> &
  {
    [K in Keys]-?: Required<Pick<T, K>> &
      Partial<Record<Exclude<Keys, K>, undefined>>;
  }[Keys];

export type ArrayItemType<T> = T extends readonly (infer I)[] ? I : never;

export const typedObjectKeys = Object.keys as <T>(
  o: T,
) => Extract<keyof T, string>[];

export type MakeMemberOptional<T, K extends keyof T> = Omit<T, K> &
  Partial<Pick<T, K>>;
export type MakeMemberRequired<T, K extends keyof T> = Omit<T, K> &
  Required<Pick<T, K>>;

type FunctionComponent<P extends {} = {}> = (
  props: P,
) => React.ReactElement | null;
export type PropsFromComponent<Component extends FunctionComponent> =
  Parameters<Component>[0];
export type PropTypeFromComponent<
  Component extends FunctionComponent,
  PropName extends keyof Parameters<Component>[0],
> = PropsFromComponent<Component>[PropName];

export type Await<T> = T extends PromiseLike<infer U> ? U : T;

export const assertUnreachable = (_: never) => {};
